import { ChangeDetectionStrategy, Component, computed, HostBinding, input } from '@angular/core';
import { neverError } from '@iac-helpers';
import {
  IacButtonIconPos,
  IacButtonSize,
  IacButtonVariant,
  IacIcon,
  IacSpinnerSize,
  IacTheme,
} from '@iac-types';
import { IconComponent } from '@iac-ui/icon';
import { SpinnerComponent } from '@iac-ui/spinner';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'button[iac-button], a[iac-button]',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, SpinnerComponent],
})
export class ButtonComponent {
  public readonly variant = input<IacButtonVariant>('flat');
  public readonly icon = input<IacIcon>();
  public readonly iconPos = input<IacButtonIconPos>('left');
  public readonly spinnerSize = input<IacSpinnerSize>(16);
  public readonly size = input<IacButtonSize>('M');
  public readonly loading = input<boolean>(false);
  public readonly theme = input<IacTheme>('on-light');
  public readonly _isDisabled = input<boolean>(false, {
    alias: 'disabled',
  });
  public readonly elastic = input<boolean>(false);
  public readonly full = input<boolean>(false);

  private readonly isDisabled = computed(() => {
    return this.loading() || this._isDisabled();
  });

  @HostBinding('class.is-elastic') get isElastic(): boolean {
    return this.elastic();
  }
  @HostBinding('class.is-full') get isFull(): boolean {
    return this.full();
  }
  @HostBinding('attr.disabled') get attrDisabled(): boolean | null {
    return this.isDisabled() ? true : null;
  }

  public readonly getWrapperClasses = computed(() => {
    const classes = [];

    if (!this.full()) {
      classes.push('flex');
    }

    if (this.elastic()) {
      classes.push('sm:inline-flex');
    }

    return classes;
  });

  public readonly getButtonClasses = computed(() => {
    const classes = [];

    const variant = this.variant();

    switch (variant) {
      case 'flat':
        classes.push('iac-button-flat');
        break;
      case 'outline':
        classes.push('iac-button-outline');
        break;
      case 'ghost':
        classes.push('iac-button-ghost');
        break;
      default:
        throw neverError(variant);
    }

    if (this.theme() === 'on-dark') {
      classes.push('iac-button-on-dark');
    } else {
      classes.push('iac-button-on-light');
    }

    if (this.isDisabled()) {
      classes.push('iac-button-disabled');
    }

    if (this.elastic()) {
      classes.push('flex', 'w-full', 'sm:inline-flex');
    } else if (this.full()) {
      classes.push('flex');
    } else {
      classes.push('inline-flex');
    }

    classes.push('leading-none', 'border', 'rounded');

    const size = this.size();

    switch (size) {
      case 'S':
        classes.push('h-8', 'gap-1');
        break;
      case 'M':
        classes.push('h-10', 'gap-1.5');
        break;
      case 'L':
        classes.push('h-12', 'gap-2');
        break;
      default:
        throw neverError(size);
    }

    if (this.variant() !== 'ghost') {
      switch (size) {
        case 'S':
          classes.push('px-2');
          break;
        case 'M':
          classes.push('px-3');
          break;
        case 'L':
          classes.push('px-4');
          break;
        default:
          throw neverError(size);
      }
    }

    return classes;
  });

  public readonly isIconContainerVisible = computed(() => {
    return this.icon() || this.loading();
  });

  public readonly getInnerClasses = computed(() => {
    return {
      'flex-row-reverse': this.iconPos() === 'right',
    };
  });
}
